import { useSession } from '@/contexts/session';
import { useQuery } from "@tanstack/react-query";

// Legacy role hierarchy (to be removed during consolidation)
const roleHierarchy = {
  Developer: 100,
  PlatformManager: 80,
  OrgManager: 60,
  OrgAdmin: 50,
  HomeManager: 40,
  HomeAdmin: 30,
  HomeStaff: 10,
  developer: 100,
  Admin: 80,
  Manager: 60,
  Staff: 40
};

// Basic role permissions (simplified version) - case-insensitive checks
const ROLE_PERMISSIONS = {
  canCreateTemplates: (role: string) => {
    const r = role.toLowerCase().replace(/_/g, '');
    return ['developer', 'platformmanager'].includes(r);
  },
  canManageOrganizations: (role: string) => {
    const r = role.toLowerCase().replace(/_/g, '');
    return ['developer', 'platformmanager'].includes(r);
  },
  canViewAllOrganizations: (role: string) => {
    const r = role.toLowerCase().replace(/_/g, '');
    return ['developer', 'platformmanager'].includes(r);
  },
  canAccessDeveloperConsole: (role: string) => {
    const r = role.toLowerCase().replace(/_/g, '');
    return r === 'developer';
  }
};

export function usePermissions() {
  const { session } = useSession();
  const user = session?.user;

  const getRoleLevel = (role: string): number => {
    return roleHierarchy[role as keyof typeof roleHierarchy] || 0;
  };

  const hasMinimumRole = (requiredRole: string): boolean => {
    if (!user) return false;
    return getRoleLevel(user.role) >= getRoleLevel(requiredRole);
  };

  const canManageUsers = (): boolean => {
    return hasMinimumRole('Admin');
  };

  const canManageTemplates = (): boolean => {
    return user?.role ? ROLE_PERMISSIONS.canCreateTemplates(user.role) : false;
  };

  // Hook for checking template create permissions via API
  const useCanCreateTemplates = () => {
    return useQuery({
      queryKey: ['/api/permissions/template-create'],
      enabled: !!user,
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    });
  };

  const canManageOrganizations = (): boolean => {
    return user?.role ? ROLE_PERMISSIONS.canManageOrganizations(user.role) : false;
  };

  const canCreateUser = (targetRole: string): boolean => {
    if (!user) return false;
    const userLevel = getRoleLevel(user.role);
    const targetLevel = getRoleLevel(targetRole);
    return targetLevel <= userLevel;
  };

  const canViewAllOrganizations = (): boolean => {
    return user?.role ? ROLE_PERMISSIONS.canViewAllOrganizations(user.role) : false;
  };

  const canAccessDeveloperFeatures = (): boolean => {
    return user?.role ? ROLE_PERMISSIONS.canAccessDeveloperConsole(user.role) : false;
  };

  const getAccessibleRoles = (): string[] => {
    if (!user) return [];
    
    const userLevel = getRoleLevel(user.role);
    const roles = [
      { role: 'Staff', level: 40 },
      { role: 'Manager', level: 60 },
      { role: 'Admin', level: 80 },
      { role: 'developer', level: 100 }
    ];
    
    return roles
      .filter(r => r.level <= userLevel)
      .map(r => r.role);
  };

  return {
    user,
    hasMinimumRole,
    canManageUsers,
    canManageTemplates,
    canManageOrganizations,
    canCreateUser,
    canViewAllOrganizations,
    canAccessDeveloperFeatures,
    getAccessibleRoles,
    getRoleLevel,
    useCanCreateTemplates
  };
}